import React, { useState } from "react";
import { View, Text, TouchableOpacity, StyleSheet, Alert } from "react-native";
import { ChevronDown, MapPin, Plus } from "lucide-react-native";
import { menuStyles } from "../components/shared/styles/menuStyles";
import { FlickOColors } from "../components/shared/styles/color";
import { getResponsiveSize } from "../components/shared/styles/globalStyles";
import HeaderBack from "../components/shared/component/HeaderBack";
import { NavigationProps } from "../types/navigation";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { UserCreDB } from "../types/user";
import { userService } from "../services/user.service";
import { DecodedToken } from "../types/token";
import { secureStoreService } from "../components/shared/services/secure-store.service";

const AddressSelectionScreen = () => {
  const navigation = useNavigation<NavigationProps>();
  const [user, setUser] = useState<UserCreDB | null>();

  const handleAddressSelection = async (addressId: string) => {
    console.log("handleAddressSelection is called with address:", addressId);
    try {
      const data: DecodedToken | null =
        await secureStoreService.getDecodedAccessToken();
      if (!data || !data?.restUserId) {
        Alert.alert("Error", "Error finding user please try again");
        return;
      }
      const user: UserCreDB | null = await userService.getCachedUser(
        data.restUserId
      );
      const currentAddresses = user?.location?.addresses || [{}];

      await userService.updateUserData(data.restUserId, {
        location: {
          addresses: currentAddresses, // Keep existing addresses
          currentId: addressId,
        },
      });
      navigation.navigate("HomeScreen");
    } catch (error) {
      console.error("Error in handleAddressSelection:", error);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      loadData();
    }, [])
  );
  const loadData = async () => {
    const data: DecodedToken | null =
      await secureStoreService.getDecodedAccessToken();
    if (!data || !data?.restUserId) {
      Alert.alert("Error", "Error finding user please try again");
      return;
    }
    const userData: UserCreDB | null = await userService.getCachedUser(
      data.restUserId
    );
    setUser(userData);
  };

  return (
    <View style={menuStyles.container}>
      <HeaderBack title="Select Delivery Location" />
      <TouchableOpacity
        style={styles.button}
        onPress={() => {
          navigation.navigate("DeliveryLocationScreen");
        }}
      >
        <MapPin size={getResponsiveSize(20)} color={FlickOColors.primary} />
        <Text style={styles.currentLocationText}>Use my current location</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.button}
        onPress={() => {
          navigation.navigate("DeliveryLocationScreen");
        }}
      >
        <Plus size={getResponsiveSize(20)} color={FlickOColors.primary} />
        <Text style={styles.currentLocationText}>Add new address</Text>
      </TouchableOpacity>

      <View style={styles.savedSection}>
        <Text style={styles.sectionTitle}>SAVED ADDRESSES</Text>
        {user?.location?.addresses?.map((addressEntry) => {
          const addressIds = Object.keys(addressEntry);
          return addressIds?.map((addressId) => {
            const address = addressEntry[addressId];
            const isSelected = user?.location?.currentId === addressId;

            return (
              <TouchableOpacity
                key={addressId}
                style={[
                  styles.addressItem,
                  isSelected && styles.selectedAddress,
                ]}
                onPress={() => handleAddressSelection(addressId)}
              >
                <View style={styles.addressContent}>
                  <View style={styles.addressHeader}>
                    <Text style={styles.addressName}>
                      {address.addressName}
                    </Text>
                    {isSelected && (
                      <View style={styles.currentLabel}>
                        <Text style={styles.currentLabelText}>
                          CURRENTLY SELECTED
                        </Text>
                      </View>
                    )}
                  </View>
                  <Text style={styles.addressText}>
                    {address.locality}, {address.city}
                  </Text>
                  <Text style={styles.distance}>21 m</Text>
                </View>
                <TouchableOpacity style={styles.menuButton}>
                  <ChevronDown
                    size={getResponsiveSize(20)}
                    color={FlickOColors.text.secondary}
                  />
                </TouchableOpacity>
              </TouchableOpacity>
            );
          });
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  button: {
    flexDirection: "row",
    alignItems: "center",
    padding: getResponsiveSize(16),
    borderBottomWidth: 1,
    borderBottomColor: FlickOColors.gray.light,
  },
  currentLocationText: {
    marginLeft: getResponsiveSize(12),
    fontSize: getResponsiveSize(16),
    color: FlickOColors.primary,
    fontWeight: "500",
  },
  savedSection: {
    padding: getResponsiveSize(16),
  },
  sectionTitle: {
    fontSize: getResponsiveSize(12),
    color: FlickOColors.text.secondary,
    marginBottom: getResponsiveSize(16),
  },
  addressItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingVertical: getResponsiveSize(12),
    borderBottomWidth: 1,
    borderBottomColor: FlickOColors.gray.light,
  },
  selectedAddress: {
    backgroundColor: FlickOColors.background.light,
  },
  addressContent: {
    flex: 1,
  },
  addressHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: getResponsiveSize(4),
  },
  addressName: {
    fontSize: getResponsiveSize(16),
    fontWeight: "500",
    color: FlickOColors.text.primary,
    marginRight: getResponsiveSize(8),
  },
  currentLabel: {
    backgroundColor: FlickOColors.success + "20",
    paddingHorizontal: getResponsiveSize(8),
    paddingVertical: getResponsiveSize(4),
    borderRadius: getResponsiveSize(4),
  },
  currentLabelText: {
    fontSize: getResponsiveSize(10),
    color: FlickOColors.success,
    fontWeight: "500",
  },
  addressText: {
    fontSize: getResponsiveSize(14),
    color: FlickOColors.text.secondary,
    marginBottom: getResponsiveSize(4),
  },
  distance: {
    fontSize: getResponsiveSize(12),
    color: FlickOColors.text.secondary,
  },
  menuButton: {
    padding: getResponsiveSize(8),
  },
});

export default AddressSelectionScreen;
